import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import {
  Divider,
  HStack,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Skeleton,
  SkeletonText,
  Text,
  VStack,
  useDisclosure
} from '@chakra-ui/react';
import { t } from 'i18next';
import { findIndex, first, isEmpty, last, sortBy } from 'lodash';
import { useQuery, useQueryClient } from 'react-query';
import { useNavigate, useParams } from 'react-router-dom';
import { Virtuoso } from 'react-virtuoso';
import Page from '../components/Page';
import LeaveChatRoomDialog from '../components/alerts/LeaveChatRoomDialog';
import leaveChatRoom from '../components/api/mutations/legacy/leaveChatRoom';
import useChatRoomMessages from '../components/api/queries/useCharRoomMessages';
import ChatMessageBubble from '../components/chats/ChatMessageBubble';
import MessageInputBox from '../components/chats/MessageInputBox';
import NewMessageAlertButton from '../components/chats/NewMessageAlertButton';
import VerifiedIcon from '../components/icons/VerifiedIcon';
import ChatRoomTradingPhotoCardInfo from '../components/tradings/ChatRoomTradingPhotoCardInfo';
import MatchedTradingPhotoCardInfo from '../components/tradings/MatchedTradingPhotoCardInfo';
// import LightColors from '../constants/LightColors';
import { useNewMessageDetection } from '../hooks/useNewMessageDetection';
import { usePushNotification } from '../presentation/chat/hooks/usePushNotification';
import { ellipsis } from '@pocapan/shared/utils/TextUtil';
import { openNewTab, refreshScreenSize } from '@pocapan/shared/utils/UIUtils';
import { queryGet } from '@pocapan/shared/utils/queryClient';
import GradientArrowRight from '../components/icons/GradientArrowRight';
import ConfirmToCompleteTradingPopup from '../presentation/review/components/ConfirmToCompleteTradingPopup';
import { isPushListeningEnabled } from '@pocapan/shared/utils/NativeUtil';

const photoCardBarStyle = {
  marginTop: '-24px',
  position: 'fixed',
  width: 'calc(100% - 24px)',
  paddingRight: '24px',
  zIndex: 990,
  maxWidth: 'calc(var(--chakra-sizes-xl) - 48px)'
};

const tradingCardSectionStyle = {
  paddingTop: '16px',
  paddingBottom: '16px',
  width: '100%'
};

const DEFAULT_TIMEOUT = isPushListeningEnabled() ? 1000 * 60 * 60 : 1000 * 10;

export default function TradingPhotoCardChatRoomPage() {
  const photoCardBarRef = useRef(null);
  const messageAreaRef = useRef(null);
  const params = useParams();
  const queryClient = useQueryClient();

  const [hasQueryExecuted, setHasQueryExecuted] = useState(false);
  const [paginatedChatRoomMessages, setPaginatedChatRoomMessages] = useState();
  const [isCompletedTradingPopupOpen, setIsCompletedTradingPopupOpen] = useState(false);

  const { isOpen, onOpen, onClose } = useDisclosure();

  useEffect(() => {
    refreshScreenSize();
  }, []);

  useEffect(() => {
    if (messageAreaRef?.current) {
      messageAreaRef.current.scrollIntoView({ block: 'end' });
    }
  }, [messageAreaRef]);

  const { data: chatRoom } = useQuery(
    ['chatRoom', params.chat_room_id],
    () => queryGet(`/chat_rooms/${params.chat_room_id}`),
    {
      enabled: !!params.chat_room_id,
      select: (r) => r.chatRoom,
      cacheTime: 0
    }
  );

  const { data, isFetching, fetchNextPage, hasNextPage } = useChatRoomMessages(
    chatRoom?.chatRoomId
  );

  usePushNotification(params.chat_room_id ?? '');

  const handlePaginatedChatRoomMessages = (paginatedData) => {
    const chatRoomMessages = paginatedData.pages
      ?.flatMap((page) => page.chatRoomMessages.map((message) => message))
      .reverse();
    setPaginatedChatRoomMessages(chatRoomMessages);
  };

  const loadMore = () => {
    if (!isFetching && hasNextPage) {
      fetchNextPage();
    }
  };
  
  useEffect(() => {
    if (!isEmpty(data)) {
      handlePaginatedChatRoomMessages(data);
    }
  }, [data]);

  const tradingUserProfile = useMemo(() => {
    const index = findIndex(chatRoom?.userProfiles, (p) => p.userId !== chatRoom?.currentUserId);
    return chatRoom?.userProfiles[index];
  }, [chatRoom]);

  const myLastUnSeenMessage = useMemo(() => {
    return first(
      sortBy(
        paginatedChatRoomMessages?.filter((message) => message.isMine && !!message.seenAt),
        (m) => {
          return -m.seenAt;
        }
      )
    );
  }, [paginatedChatRoomMessages]);

  // scrollToBottom 함수를 useCallback으로 정의
  const scrollToBottom = useCallback((_isSmooth = true) => {
    if (messageAreaRef?.current) {
      window.scrollTo({
        left: 0,
        top: document.body.scrollHeight,
        behavior: 'smooth'
      });
    }
  }, []);

  // 새 메시지 감지 훅 사용
  const { showNewMessageButton, handleAtBottomStateChange, handleNewMessageButtonClick } =
    useNewMessageDetection(paginatedChatRoomMessages, scrollToBottom);

  useEffect(() => {
    const refetchMessges = setInterval(() => {
      refetchMessages();
    }, DEFAULT_TIMEOUT);
    setTimeout(() => {
      scrollToBottom(true);
    }, 300);
    return () => {
      clearInterval(refetchMessges);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!paginatedChatRoomMessages || isEmpty(paginatedChatRoomMessages)) return;
    if (!hasQueryExecuted) return;

    const lastMessage = last(paginatedChatRoomMessages);
    if (lastMessage.isMine) {
      // setTimeout(() => { scrollToBottom(true) }, 100);
    }
  }, [paginatedChatRoomMessages]);

  useEffect(() => {
    if (!hasQueryExecuted && !!paginatedChatRoomMessages) {
      setHasQueryExecuted(true);
      if (paginatedChatRoomMessages.length > 7) {
        // setTimeout(() => { scrollToBottom(false) }, 100);
      }
    }
  }, [hasQueryExecuted, paginatedChatRoomMessages]);

  const refetchMessages = () => {
    queryClient.invalidateQueries(['ChatRoomMessages', params.chat_room_id]);
  };

  const onSentMessage = () => {
    refetchMessages();
    // fetch 되어 message에 반영되기 전에 bottom으로 내려봤자 동작을 하지 않음
    scrollToBottom(true);
  };

  const onClickDialogButton = async () => {
    if (chatRoom?.chatRoomId) {
      const response = await leaveChatRoom(chatRoom?.chatRoomId);
      if (response.success) {
        queryClient.invalidateQueries(['chatRooms']);
      }
    }
    window.history.back();
  };

  const noticeMessage = () => {
    // TODO : Handle if I left this chatroom AND Blocked User
    if (chatRoom?.chatRoomUsers?.some((u) => !!u.deletedAt)) {
      return t('tradingPhotoCardChatRoom.noticeDeletedUser');
    }
    if (chatRoom?.chatRoomUsers?.some((u) => !!u.leftAt)) {
      return t('tradingPhotoCardChatRoom.noticeLeftUser');
    }
  };

  const isTrading =
    chatRoom?.subjectType === 'TradingPhotoCard' ||
    chatRoom?.subjectType === 'MatchedTradingPhotoCard';

  const navigateUserTradingProfile = (userProfile) => {
    const currentPath = window.location.pathname;
    const userProfilePath = isTrading
      ? `/trading/user_profile/${userProfile?.userProfileId}`
      : `/feed/user_profile/${userProfile?.userProfileId}`;
    if (currentPath === userProfilePath) return;

    if (!isEmpty(userProfile?.userProfileId)) {
      openNewTab(userProfilePath);
    }
  };

  // 채팅이 10개 이상이고 채팅방 생성이 지금으로부터 3일전일 때
  const isShowCompletedTradingButton =
    paginatedChatRoomMessages?.length > 10 &&
    new Date(chatRoom?.createdAt) < new Date(Date.now() - 3 * 24 * 60 * 60 * 1000);

  return (
    <>
      <Page
        title={t('tradingPhotoCardChatRoom.pageTitle')}
        titleElement={
          <HStack
            spacing={1}
            justifyContent={'center'}
            justifySelf={'center'}
            flexGrow={1}
            px={3}
            onClick={() => navigateUserTradingProfile(tradingUserProfile)}
          >
            <div className="text-black dark:text-white text-[17px] font-bold max-w-[166px] truncate leading-[20px] text-center">
              {ellipsis(tradingUserProfile?.nickname, 20)}
            </div>
            {tradingUserProfile?.identified && <VerifiedIcon width={14} height={14} />}
          </HStack>
        }
        showTitle={false}
        showBackButton={true}
        navigationElement={
          <MeatBallMenus zIndex={9999} chatRoomId={params.chat_room_id} onOpen={onOpen} />
        }
        style={{ height: 'auto' }}
        borderBottom={true}
      >
        <div className="bg-background" ref={photoCardBarRef} style={photoCardBarStyle}>
          <Divider style={{ marginLeft: '-24px', marginRight: '-24px' }} />

          <TradingPhotoCardSection
            chatRoom={chatRoom}
            tradingUserProfile={tradingUserProfile}
            setIsCompletedTradingPopupOpen={setIsCompletedTradingPopupOpen}
            isShowCompletedTradingButton={isShowCompletedTradingButton}
          />
          <Divider
            style={{
              marginLeft: '-24px',
              marginRight: '-24px',
              width: 'calc(100% + 48px)'
            }}
          />
        </div>
        <div
          ref={messageAreaRef}
          className="flex flex-col min-h-[calc(100vh-100px)]"
          style={{
            paddingBottom: 'calc(env(safe-area-inset-bottom) + 70px)',
            paddingTop: isTrading ? (photoCardBarRef?.current?.clientHeight || 0) - 12 : 0
          }}
        >
          <NoticeText
            key="notice"
            tradingStatus={chatRoom?.tradingStatus}
            noticeMessage={noticeMessage()}
          />
          {/* {
            !isEmpty(paginatedChatRoomMessages) &&
            <EasyVirtualized
              useParentScrollElement={true}
              onLoadMore={loadMore}
              hasMore={hasNextPage}
              scrollReverse={true}
              overscanRowCount={20}
              loader={<Center marginTop={"8px"}><Spinner size='sm' color="primary" marginRight={"20px"}/></Center>}
            >
              {
                paginatedChatRoomMessages.map((message, index) => {
                  return(
                    <MessageBubble
                      key={message.chatRoomMessageId}
                      chatRoom={chatRoom}
                      message={message}
                      nextMessage={(index + 1 < paginatedChatRoomMessages.length ? paginatedChatRoomMessages[index + 1] : message)}
                      myLastUnSeenMessage={myLastUnSeenMessage}
                    />
                  )
                })
              }
            </EasyVirtualized>
          } */}
          <Virtuoso
            useWindowScroll
            style={{ height: '100%' }}
            startReached={loadMore}
            overscan={480}
            data={paginatedChatRoomMessages}
            initialTopMostItemIndex={(paginatedChatRoomMessages?.length || 0) - 1}
            atBottomStateChange={handleAtBottomStateChange}
            itemContent={(index, message) => {
              // 메시지가 존재하지 않으면 빈 div 반환
              if (!message) {
                return <div />;
              }

              const previousMessage = index > 0 ? paginatedChatRoomMessages[index - 1] : undefined;
              const isFirstMessage = index === 0;

              return (
                <ChatMessageBubble
                  chatRoom={chatRoom}
                  message={message}
                  previousMessage={previousMessage}
                  myLastUnSeenMessage={myLastUnSeenMessage}
                  isFirstMessage={isFirstMessage}
                  styleVariant="trading"
                />
              );
            }}
          />
        </div>
      </Page>

      <MessageInputBox
        tradingUserProfile={tradingUserProfile}
        messageAreaRef={messageAreaRef}
        onSentMessage={() => onSentMessage()}
        disabled={!isEmpty(noticeMessage)}
        isTrading={isTrading}
      />

      {/* 새 메시지 알림 버튼 */}
      <NewMessageAlertButton
        show={showNewMessageButton}
        onClick={handleNewMessageButtonClick}
        styleVariant="trading"
      />

      <LeaveChatRoomDialog
        isOpen={isOpen}
        onClose={onClose}
        onClickDialogButton={onClickDialogButton}
      />
      {isCompletedTradingPopupOpen && (
        <CompletedTradingPopup
          setIsCompletedTradingPopupOpen={setIsCompletedTradingPopupOpen}
          chatRoom={chatRoom}
          tradingUserProfile={tradingUserProfile}
        />
      )}
    </>
  );
}

function NoticeText({ tradingStatus, noticeMessage }) {
  if (!tradingStatus) return <></>;

  if (isEmpty(noticeMessage)) return <></>;

  return (
    <div style={{ width: '100%', paddingTop: '20px' }} className="text-center">
      <Text className="text-main-light-3 dark:text-main-dark-3 text-[12px]">{noticeMessage}</Text>
    </div>
  );
}

function MeatBallMenus({ chatRoomId, onOpen }) {
  const navigate = useNavigate();
  return (
    <Menu autoSelect={false} zIndex={9999}>
      <MenuButton>
        <Text fontSize={'18px'} fontWeight={600}>
          {'⋯'}
        </Text>
      </MenuButton>
      <MenuList>
        <MenuItem onClick={() => navigate(`/trading_reports?chatRoomId=${chatRoomId}`)}>
          {t('report')}
        </MenuItem>
        <MenuItem onClick={() => onOpen()}>{t('leave')}</MenuItem>
      </MenuList>
    </Menu>
  );
}

function TradingPhotoCardSection({
  chatRoom,
  tradingUserProfile,
  setIsCompletedTradingPopupOpen,
  isShowCompletedTradingButton
}) {
  const isSelling = chatRoom?.currentUserId === chatRoom?.tradingPhotoCard?.userId;

  if (!chatRoom) {
    return (
      <div
        className="flex bg-background-light dark:bg-background-dark"
        style={tradingCardSectionStyle}
      >
        <div className="flex-1 w-full">
          <VStack align={'flex-start'}>
            <HStack align={'flex-start'}>
              <Skeleton
                width={'40px'}
                height={'62px'}
                startColor="rgba(0, 0, 0, 0.04)"
                endColor="rgba(0, 0, 0, 0.02)"
              />
              <VStack paddingTop={'2px'}>
                <SkeletonText
                  noOfLines={2}
                  width={'75px'}
                  startColor="rgba(0, 0, 0, 0.04)"
                  endColor="rgba(0, 0, 0, 0.02)"
                />
              </VStack>
            </HStack>
          </VStack>
        </div>
      </div>
    );
  }

  if (chatRoom?.matchedTradingPhotoCard) {
    return (
      <div className="bg-background-light dark:bg-background-dark">
        {/* 채팅이 10개 이상 + 채팅방 생성으로부터 3일이 지난 경우 거래완료하기 버튼 표시 */}
        {isShowCompletedTradingButton && (
          <CompletedTradingPhotoCardSection
            category="exchange"
            setIsCompletedTradingPopupOpen={setIsCompletedTradingPopupOpen}
          />
        )}
        <div className="w-full py-[16px]">
          <MatchedTradingPhotoCardInfo
            source={chatRoom?.matchedTradingPhotoCard?.sourceCard}
            target={chatRoom?.matchedTradingPhotoCard?.targetCard}
          />
        </div>
      </div>
    );
  }

  if (chatRoom?.tradingPhotoCard) {
    return (
      <div className="flex w-full flex-col bg-background-light dark:bg-background-dark">
        {/* 구매 포카이고 채팅이 10개 이상 + 채팅방 생성으로부터 3일이 지난 경우 거래완료하기 버튼 표시 */}
        {!isSelling && isShowCompletedTradingButton && (
          <CompletedTradingPhotoCardSection
            category="sale"
            setIsCompletedTradingPopupOpen={setIsCompletedTradingPopupOpen}
          />
        )}

        <div className="w-full py-[16px]">
          <ChatRoomTradingPhotoCardInfo
            chatRoomId={chatRoom.chatRoomId}
            tradingPhotoCard={chatRoom?.tradingPhotoCard}
            isMine={chatRoom.currentUserId === chatRoom?.tradingPhotoCard?.userId}
            tradingUserProfile={tradingUserProfile}
            showPrice={true}
          />
        </div>
      </div>
    );
  }
}

const CompletedTradingPhotoCardSection = ({ setIsCompletedTradingPopupOpen, category }) => {
  return (
    <div className="w-screen relative left-[-24px]">
      <div className="w-full bg-separator1 py-[14px] px-[24px] flex items-center justify-between">
        <p className="text-text-main-lv4 text-[13px] font-semibold leading-none">
          {category === 'exchange'
            ? t('tradingPhotoCardChatRoom.completedExchangeDescription')
            : t('tradingPhotoCardChatRoom.completedSaleDescription')}
        </p>
        <button
          type="button"
          onClick={() => setIsCompletedTradingPopupOpen(true)}
          className="flex items-center gap-[4px]"
        >
          <span className="text-[15px] font-semibold bg-gradient-to-r from-[#458FFF] to-[#CF6AFF] bg-clip-text text-transparent leading-none">
            {t('tradingPhotoCardChatRoom.completedTradingButton')}
          </span>
          <GradientArrowRight size={20} />
        </button>
      </div>
    </div>
  );
};

const CompletedTradingPopup = ({
  setIsCompletedTradingPopupOpen,
  chatRoom,
  tradingUserProfile
}) => {
  const navigate = useNavigate();
  const handleClosePopup = () => {
    setIsCompletedTradingPopupOpen(false);
  };

  const handleNavigateToReviewPage = () => {
    if (chatRoom.subjectType === 'TradingPhotoCard') {
      navigate(
        `/reviews/add-review?chatRoomId=${chatRoom.chatRoomId}&traderUserProfileId=${tradingUserProfile?.userProfileId}&reviewableId=${chatRoom.tradingPhotoCard.tradingPhotoCardId}&reviewableType=${chatRoom.subjectType}`
      );
    } else {
      navigate(
        `/reviews/add-review?chatRoomId=${chatRoom.chatRoomId}&traderUserProfileId=${tradingUserProfile?.userProfileId}&reviewableId=${chatRoom.matchedTradingPhotoCard.matchedTradingPhotoCardId}&reviewableType=${chatRoom.subjectType}`
      );
    }
  };

  return (
    <ConfirmToCompleteTradingPopup
      handleClosePopup={handleClosePopup}
      handleNavigateToReviewPage={handleNavigateToReviewPage}
      category={chatRoom.subjectType}
    />
  );
};
